// src/components/ExpressionWidget.tsx
/**
 * ExpressionWidget Component (T-17)
 * 
 * Floating widget for creating and editing mathematical expression objects.
 * Features live preview, validation, and drag-to-place functionality.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  addExpressionObject,
  updateExpressionObject,
  hideExpressionWidget,
  updateWidgetExpression,
  selectExpressionWidget,
  selectExpressionEnvironment
} from '../store/slices/expressionsSlice';
import { evaluate, getVariableReferences, parse } from '../lib/expressions';
import { toNumber, formatFractionForMath } from '../lib/fractions';
import { formatNumber, formatNumberWithPrecision, parseNumberFlexible, LocaleCode } from '../lib/locale';
import { selectLocale } from '../store/slices/preferencesSlice';
import { formatFraction } from '../lib/fractions';
import { Fraction } from '../types/redux';
import MathRenderer from './MathRenderer';

// Helper function to detect if input looks like a fraction
const detectInputFormat = (input: string): 'fraction' | 'decimal' | 'integer' => {
  const trimmed = input.trim();
  
  // Check if it contains a slash (fraction format)
  if (trimmed.includes('/')) {
    return 'fraction';
  }
  
  // Check if it contains a decimal point
  if (trimmed.includes('.')) {
    return 'decimal';
  }
  
  // Default to integer
  return 'integer';
};

// Helper function to render value based on original format
const renderVariableValue = (value: Fraction): string => {
  // For the expression widget, we'll prioritize showing fractions when they're simple
  // Check if this is a simple fraction that should be displayed as such
  if (value.d !== 1 && value.d <= 100) {
    // Use KaTeX formatting for fractions
    return formatFractionForMath(value);
  }
  
  // For integers or large denominators, show as decimal
  const numValue = toNumber(value);
  return Number.isInteger(numValue) ? numValue.toString() : numValue.toString();
};

interface ExpressionWidgetProps {
  /** Callback to convert screen coordinates to number line value */
  screenToValue?: (screenPos: { x: number; y: number }) => number;
  /** Current number line domain for validation */
  domain?: { start: number; end: number };
}

/**
 * ExpressionWidget provides an interface for creating and editing expression objects
 */
export default function ExpressionWidget({ 
  screenToValue,
  domain = { start: -10, end: 10 }
}: ExpressionWidgetProps) {
  const dispatch = useDispatch();
  const widget = useSelector(selectExpressionWidget);
  const environment = useSelector(selectExpressionEnvironment);
  const locale = useSelector(selectLocale);
  
  const [localExpression, setLocalExpression] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewValue, setPreviewValue] = useState<number | null>(null);
  const [variables, setVariables] = useState<string[]>([]);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Sync with Redux widget state
  useEffect(() => {
    if (widget.isVisible) {
      setLocalExpression(widget.currentExpression);
      validateExpression(widget.currentExpression);
      
      // Focus input when widget becomes visible
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setLocalExpression('');
      setValidationError(null);
      setPreviewValue(null);
      setVariables([]);
    }
  }, [widget.isVisible, widget.currentExpression]);

  // Validate expression and update preview
  const validateExpression = (expression: string) => {
    if (!expression.trim()) {
      setValidationError(null);
      setPreviewValue(null);
      setVariables([]);
      return;
    }

    try {
      // Parse variables
      const vars = getVariableReferences(parse(expression));
      setVariables(vars);

      // Evaluate expression
      const result = evaluate(expression, environment);
      
      if (result.error) {
        setValidationError(result.error);
        setPreviewValue(null);
      } else if (result.value) {
        setValidationError(null);
        setPreviewValue(toNumber(result.value));
      } else {
        setValidationError('Invalid expression');
        setPreviewValue(null);
      }
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : 'Parse error');
      setPreviewValue(null);
      setVariables([]);
    }
  };

  // Handle expression input changes
  const handleExpressionChange = (value: string) => {
    setLocalExpression(value);
    dispatch(updateWidgetExpression(value));
    validateExpression(value);
  };

  // Handle saving the expression
  const handleSave = () => {
    if (!localExpression.trim() || validationError) {
      return;
    }

    if (widget.mode === 'edit' && widget.targetObjectId) {
      // Update existing object
      dispatch(updateExpressionObject({
        id: widget.targetObjectId,
        updates: { expression: localExpression.trim() }
      }));
    } else {
      // Create new object
      dispatch(addExpressionObject({
        expression: localExpression.trim(),
        screenPosition: widget.position,
        equivalentDenominator: widget.equivalentDenominator ?? undefined,
      }));
    }

    dispatch(hideExpressionWidget());
  };

  // Handle canceling
  const handleCancel = () => {
    dispatch(hideExpressionWidget());
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  // T-25: Format expression values with locale support
  const formatExpressionValue = useCallback((value: number | Fraction): string => {
    if (typeof value === 'number') {
      // T-25: Format numeric values with locale
      return formatNumberWithPrecision(value, locale, 4);
    } else {
      // For fraction values, format with locale support
      return formatFraction(value, {
        mixed: true,
        simplify: true,
        locale: locale // T-25: Pass locale to fraction formatting
      });
    }
  }, [locale]);

  // T-25: Parse expression input with locale support
  const parseExpressionInput = useCallback((input: string): string => {
    // Allow flexible number parsing in expressions
    let normalized = input;
    
    // Simple approach: if locale uses comma, replace commas with dots for parsing
    if (locale === 'fr-FR' || locale === 'de-DE' || locale === 'es-ES') {
      // For comma locales, convert commas to dots for internal parsing
      normalized = input.replace(/(\d),(\d)/g, '$1.$2');
    }
    
    return normalized;
  }, [locale]);

  // Close widget when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target as Node)) {
        dispatch(hideExpressionWidget());
      }
    };

    if (widget.isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [widget.isVisible, dispatch]);

  // Don't render if not visible
  if (!widget.isVisible) {
    return null;
  }

  // Calculate widget position (ensure it stays on screen)
  const calculatePosition = () => {
    const x = Math.max(10, Math.min(widget.position.x, window.innerWidth - 320));
    const y = Math.max(10, Math.min(widget.position.y, window.innerHeight - 200));
    return { x, y };
  };

  const position = calculatePosition();

  // Check if expression can be placed in current domain
  const isInDomain = previewValue !== null && 
    previewValue >= domain.start && 
    previewValue <= domain.end;

  // Use Portal to render at document.body level to avoid transform container issues with position: fixed
  return createPortal(
    <div
      ref={widgetRef}
      className="expression-widget"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 1000,
        backgroundColor: '#FFFFFF',
        border: '2px solid #E5E7EB',
        borderRadius: '8px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        padding: '16px',
        minWidth: '300px',
        maxWidth: '400px',
      }}
    >
      {/* Header */}
      <div className="widget-header" style={{ marginBottom: '12px' }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '16px', 
          fontWeight: '600',
          color: '#374151'
        }}>
          {widget.mode === 'edit' ? 'Edit Expression' : 'Add Expression'}
        </h3>
      </div>

      {/* Expression input */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '500',
          color: '#6B7280',
          marginBottom: '4px'
        }}>
          Expression
        </label>
        <input
          ref={inputRef}
          type="text"
          value={localExpression}
          onChange={(e) => handleExpressionChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={getLocalizedPlaceholder(locale)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: `2px solid ${validationError ? '#EF4444' : '#D1D5DB'}`,
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'monospace',
            backgroundColor: validationError ? '#FEF2F2' : '#FFFFFF',
          }}
        />
      </div>

      {/* Live preview */}
      {localExpression && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ 
            padding: '8px 12px',
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '4px'
          }}>
            {/* Rendered expression */}
            <div style={{ marginBottom: '4px' }}>
              <span style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>
                Rendered: 
              </span>
              <MathRenderer 
                expression={localExpression}
                style={{ fontSize: '14px', marginLeft: '8px' }}
              />
            </div>

            {/* Variables used */}
            {variables.length > 0 && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>
                  Variables: 
                </span>
                <span style={{ fontSize: '12px', marginLeft: '8px' }}>
                  {variables.join(', ')}
                </span>
              </div>
            )}

            {/* Current value */}
            {previewValue !== null && (
              <div>
                <span style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>
                  Position: 
                </span>
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: '600',
                  marginLeft: '8px',
                  color: isInDomain ? '#059669' : '#DC2626'
                }}>
                  {previewValue.toFixed(3)}
                  {!isInDomain && ' (outside view)'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error display */}
      {validationError && (
        <div style={{
          marginBottom: '12px',
          padding: '8px 12px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#DC2626'
        }}>
          {validationError}
        </div>
      )}

      {/* Variable environment info */}
      {Object.keys(environment).length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <details>
            <summary style={{ 
              fontSize: '12px', 
              color: '#6B7280', 
              cursor: 'pointer',
              fontWeight: '500'
            }}>
              Current Variables ({Object.keys(environment).length})
            </summary>
            <div style={{ 
              marginTop: '4px',
              fontSize: '11px',
              fontFamily: 'monospace',
              color: '#374151'
            }}>
              {Object.entries(environment).map(([name, value]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MathRenderer expression={name} />
                  <span>=</span>
                  <MathRenderer expression={renderVariableValue(value as Fraction)} />
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        justifyContent: 'flex-end',
        marginTop: '16px'
      }}>
        <button
          onClick={handleCancel}
          style={{
            padding: '6px 12px',
            fontSize: '14px',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            backgroundColor: '#FFFFFF',
            color: '#374151',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!localExpression.trim() || !!validationError}
          style={{
            padding: '6px 12px',
            fontSize: '14px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: (!localExpression.trim() || validationError) ? '#9CA3AF' : '#3B82F6',
            color: '#FFFFFF',
            cursor: (!localExpression.trim() || validationError) ? 'not-allowed' : 'pointer',
            fontWeight: '500'
          }}
        >
          {widget.mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>

      {/* Help text */}
      <div style={{ 
        marginTop: '12px',
        fontSize: '11px',
        color: '#9CA3AF',
        borderTop: '1px solid #F3F4F6',
        paddingTop: '8px'
      }}>
        <strong>Tips:</strong> Use +, -, *, /, ^ for operators. Variables: a-z. 
        Implicit multiplication: 2a = 2*a, 3(x+1) = 3*(x+1). 
        Press Enter to save, Esc to cancel.
      </div>
    </div>,
    document.body
  );
}

// T-25: Helper function for localized placeholders
function getLocalizedPlaceholder(locale: LocaleCode): string {
  const examples = {
    'en-GB': 'e.g., 2a + 1/3, x^2, 3(x+1)',
    'en-US': 'e.g., 2a + 1/3, x^2, 3(x+1)',
    'de-DE': 'z.B. 2a + 1/3, x^2, 3(x+1)',
    'fr-FR': 'ex. 2a + 1/3, x^2, 3(x+1)',
    'es-ES': 'ej. 2a + 1/3, x^2, 3(x+1)'
  };
  
  return examples[locale] || examples['en-GB'];
}